<template>
  <base-dialog
    :show="showDialog"
    :title="
      dialogAction === 'create' ? $t('page.users.title.addUser') : $t('common.action.editUser')
    "
    :actionName="$t('common.action.save')"
    @action="dialogAction === 'create' ? addUser() : updateUser()"
    :actionDisabled="isValid()"
    :max-width="$vuetify.display.mdAndUp ? '30%' : '100%'"
    :isLoading="loading"
    :showCancel="$t('common.action.cancel')"
    buttonColor="blue"
    @close="showDialog = false"
  >
    <template #content>
      <div class="d-flex align-center">
        <span class="app-field-label">{{ $t("common.action.email") }}</span>
      </div>
      <v-text-field v-model="email" :rules="emailRules" :disabled="dialogAction === 'edit'" />
      <div class="d-flex align-center">
        <span class="app-field-label">{{ $t("common.action.fullName") }}</span>
      </div>
      <v-text-field v-model="name" :rules="nameRules" :disabled="dialogAction === 'edit'" />
      <div class="d-flex align-center">
        <span class="app-field-label">{{ $t("page.users.title.accessLevel") }}</span>
      </div>
      <v-select
        v-model="level"
        :items="accessLevels"
        :rules="selectRules"
        :placeholder="$t('page.users.title.accessLevel')"
        persistent-placeholder
      />
      <terminal-locations-multi-select
        v-if="hasLocationBasedInventory"
        v-model="assignedLocationIds"
        :disabled="loading"
        :label="$t('page.locations.locations')"
      />
    </template>
  </base-dialog>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import i18n from "@/app/plugins/i18n";
import BaseDialog from "@/shared/components/Common/BaseDialog.vue";
import TerminalLocationsMultiSelect from "@/shared/components/Common/TerminalLocationsMultiSelect.vue";
import { useValidation } from "@/shared/composables/validation";
import { useUserStore } from "@/users/stores/users";
import { useAccountStore } from "@/account/stores/account";
import { InviteUser } from "@/auth/types/register";
import { Emit } from "@/shared/enums/emit.enum";

const { name, email, emailRules, nameRules, selectRules } = useValidation();
const level = ref();
const assignedLocationIds = ref<string[] | null>(null);
const loading = ref(false);
const showDialog = ref(false);
const uid = ref("");
const accessLevels = [
  i18n.global.t("page.users.userRoles.owner"),
  i18n.global.t("page.users.userRoles.admin"),
  i18n.global.t("page.users.userRoles.staff"),
];
const emit = defineEmits([Emit.UPDATED]);
const hasLocationBasedInventory = computed(() => {
  const account = useAccountStore().account;
  return account?.accountSettings?.inventory?.location_based_inventory_enabled === true;
});

defineProps({
  dialogAction: {
    type: String,
    default: i18n.global.t("common.action.create"),
  },
});

// Methods
const resetForm = () => {
  name.value = "";
  email.value = "";
  level.value = null;
  assignedLocationIds.value = null;
};

const open = async (id?: string) => {
  resetForm();
  showDialog.value = true;
  loading.value = true;

  if (id) {
    uid.value = id;
    const user = await useUserStore().retrieveUser(id);
    const {
      email: userEmail,
      displayName,
      role,
      assignedLocationIds: userAssignedLocationIds,
    } = user;
    email.value = userEmail;
    name.value = displayName;
    level.value = i18n.global.t(`page.users.userRoles.${role}`);
    assignedLocationIds.value = userAssignedLocationIds ?? null;
  }
  loading.value = false;
};

defineExpose({
  open,
});

const isValid = () => {
  return !(name.value && email.value && level.value);
};

const displayName = computed(() => String(name.value || "").trim());

const addUser = () => {
  loading.value = true;
  const payload: InviteUser = {
    emails: [email.value],
    role: level.value,
    displayName: displayName.value,
    ...(hasLocationBasedInventory.value ? { assignedLocationIds: assignedLocationIds.value } : {}),
  };
  useUserStore()
    .inviteUser(payload)
    .finally(() => {
      loading.value = false;
      emit(Emit.UPDATED);
      showDialog.value = false;
    });
};
const updateUser = () => {
  loading.value = true;
  const payload = {
    role: level.value.toLowerCase(),
    ...(hasLocationBasedInventory.value ? { assignedLocationIds: assignedLocationIds.value } : {}),
  };
  useUserStore()
    .updateUser(uid.value, payload)
    .finally(() => {
      loading.value = false;
      emit(Emit.UPDATED);
      showDialog.value = false;
    });
};
</script>
